import type { ProgrammeMiniResponse } from "../api/parcoursApi";

interface LegacyEvent {
  time: string;
  /** Heure de fin "HH:MM" (optionnelle). */
  endTime?: string;
  /** Date de fin ISO, uniquement si différente du jour de début (programme sur plusieurs jours). */
  endDate?: string;
  title: string;
  where: string;
  description?: string;
  image?: string;
  tag?: string;
}

interface LegacySillon {
  date: string;
  events: LegacyEvent[];
}

/** Groups programmes by date into the `[{events: [...]}, ...]` shape consumed by the legacy detail page. */
export function mapProgrammesToSillons(programmes: ProgrammeMiniResponse[]): LegacySillon[] {
  const byDate = new Map<string, LegacyEvent[]>();
  for (const p of programmes) {
    const date = p.date ?? "";
    const where = p.lieu?.name ?? p.addressName ?? "";
    const start = (p.time ?? "").slice(0, 5);
    const end = (p.endTime ?? "").slice(0, 5);
    // Keep `time` as just the start (fixed 80px slot on the legacy renderer).
    // End time/date travel on separate fields so the legacy renderer stays untouched
    // while future improvements can pick them up.
    const event: LegacyEvent = {
      time: (p.time ?? "").slice(0, 5),
      endTime: p.endTime ? p.endTime.slice(0, 5) : undefined,
      endDate: p.endDate && p.endDate !== date ? p.endDate : undefined,
      title: p.title,
      where,
      description: p.subtitle,
      image: p.imageLink,
      tag: p.tag,
    };
    const list = byDate.get(date) ?? [];
    list.push(event);
    byDate.set(date, list);
  }
  // Sort each day's events by time, then sort days chronologically.
  return Array.from(byDate.entries())
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([date, events]) => ({
      date,
      events: events.sort((a, b) => a.time.localeCompare(b.time)),
    }));
}

interface LegacyDocument {
  title: string;
  url: string;
  type: string;
}

/** Extensions de fichiers téléchargeables : le nom du fichier sert alors de titre. */
const FILE_EXT_RE = /\.(pdf|docx?|xlsx?|pptx?|odt|ods|csv|txt|zip|rar|7z|jpe?g|png|gif|webp|svg|mp3|mp4|mov)$/i;

/** Transforms simple resource URLs into the {title, url, type} structure the legacy page expects. */
export function mapResourcesToDocuments(resources: string[]): LegacyDocument[] {
  return resources
    .filter((r) => r && r.trim().length > 0)
    .map((raw) => {
      const url = raw.trim();
      let pathname = url;
      let host = "";
      try {
        const u = new URL(url, "https://placeholder.invalid");
        pathname = u.pathname;
        if (u.hostname !== "placeholder.invalid") host = u.hostname.replace(/^www\./, "");
      } catch {
        // URL invalide : on garde la chaîne brute comme chemin.
      }
      const lastSegment = pathname.split(/[\\/]/).filter(Boolean).pop() ?? "";
      let filename = lastSegment;
      try {
        filename = decodeURIComponent(lastSegment);
      } catch {
        // Séquence % invalide : on garde le segment tel quel.
      }
      const extMatch = filename.match(FILE_EXT_RE);
      if (extMatch) {
        return { title: filename, url, type: extMatch[1].toUpperCase() };
      }
      // Simple lien web (pas un fichier) : un libellé lisible plutôt que le
      // dernier segment de l'URL (qui donnait des titres du genre "---").
      return { title: "Site internet", url, type: host || "Lien" };
    });
}
